import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { statsQuery } from '@/api/access'
import { BarList, ColumnChart } from '@/app/access/charts'
import { EntryShell, type EntryContext } from '@/app/access/entry-shell'
import {
  busiestHour,
  clockTime,
  daysOf,
  decimal,
  durationText,
  localDate,
  presetRange,
  ranked,
  refusalText,
  STAT_PRESETS,
  type StatPreset,
} from '@/domain/access'
import { errorText } from '@/domain/errors'
import { formatDate, t } from '@/i18n'
import { InlineBanner } from '@/ui/inline-banner'
import { NeutralSegmented } from '@/ui/segmented'
import { SectionHeader } from '@/ui/section-header'
import { LoadingBlock } from '@/ui/spinner'
import { StatTile } from '@/ui/stat-tile'

export const Route = createFileRoute('/_app/c/$orgId/entry/stats')({
  validateSearch: (search: Record<string, unknown>): { range?: StatPreset } =>
    typeof search['range'] === 'string' && (STAT_PRESETS as readonly string[]).includes(search['range'])
      ? { range: search['range'] as StatPreset }
      : {},
  component: Stats,
})

function Stats() {
  const { orgId } = Route.useParams()
  return (
    <EntryShell orgId={orgId} tab="stats" allowed={(can) => can.visits}>
      {(ctx) => <StatsScreen key={ctx.branch.id} {...ctx} />}
    </EntryShell>
  )
}

const hh = (hour: number) => String(hour).padStart(2, '0')

/// «12.10» — a day under a column.
const dayTick = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`

/// «Статистика»: a range (today, 7 or 30 days, in the club's zone), four
/// figures, the hourly curve a manager plans shifts against, visits per day,
/// how people come in and why the door said no. All from the server's
/// numbers (computed from visits each time, specs/access.md §7).
function StatsScreen({ orgId, branch, timeZone }: EntryContext) {
  const { range = 'week' } = Route.useSearch()
  const navigate = Route.useNavigate()
  const today = localDate(new Date(), timeZone)
  const { from, to } = presetRange(range, today)
  const stats = useQuery(statsQuery(orgId, branch.id, from, to, timeZone))
  const s = t.access.stats

  const controls = (
    <NeutralSegmented
      label={s.range}
      className="self-start"
      value={range}
      options={STAT_PRESETS.map((preset) => ({ value: preset, label: s.presets[preset] }))}
      onChange={(next) => void navigate({ search: next === 'week' ? {} : { range: next }, replace: true })}
    />
  )

  if (stats.isPending) {
    return (
      <div className="flex flex-col gap-6">
        {controls}
        <LoadingBlock label={t.app.loading} />
      </div>
    )
  }
  if (stats.isError) {
    return (
      <div className="flex flex-col gap-6">
        {controls}
        <InlineBanner kind="error" message={errorText(stats.error)} action={{ label: t.app.retry, onClick: () => void stats.refetch() }} />
      </div>
    )
  }

  const data = stats.data
  const peakHour = busiestHour(data.hourly)
  const visitsByDay = new Map(data.days.map((day) => [day.date, day.visits]))
  const days = daysOf(data.from, data.to)
  const dayValues = days.map((day) => visitsByDay.get(day) ?? 0)
  const busiestDay = dayValues.indexOf(Math.max(0, ...dayValues))
  const every = days.length > 14 ? 5 : 1
  const peakWhen = data.peak.at
    ? range === 'today'
      ? clockTime(data.peak.at, timeZone)
      : `${formatDate(localDate(new Date(data.peak.at), timeZone))}, ${clockTime(data.peak.at, timeZone)}`
    : null

  return (
    <div className={`flex flex-col gap-8 ${stats.isPlaceholderData ? 'opacity-60 transition-opacity' : ''}`}>
      {controls}

      <dl className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label={s.visits} value={String(data.visits)} />
        <StatTile label={s.unique} value={String(data.uniqueClients)} />
        <StatTile
          label={s.peak}
          value={data.peak.count > 0 ? String(data.peak.count) : '—'}
          note={peakWhen ?? s.peakNone}
        />
        <StatTile
          label={s.stay}
          value={data.stay.average !== null ? durationText(data.stay.average) : '—'}
          note={data.stay.median !== null ? s.stayMedian(durationText(data.stay.median)) : s.stayNone}
        />
      </dl>

      <Panel title={s.hourly} aside={s.hourlySub}>
        {peakHour === null ? (
          <p className="px-4 py-6 text-callout text-muted">{s.hourlyEmpty}</p>
        ) : (
          <div className="px-4 pt-4 pb-2">
            <ColumnChart
              values={data.hourly}
              tickLabel={hh}
              showTick={(hour) => hour % 3 === 0}
              describe={(hour) => s.hourlyLabel(hour, decimal(data.hourly[hour] ?? 0))}
              summary={`${s.hourly}. ${s.hourlySummary(`${hh(peakHour)}:00`)}`}
              format={decimal}
              mark={peakHour}
              table={{
                caption: s.hourly,
                head: [s.tableHour, s.tableValue],
                rows: data.hourly.map((value, hour) => [`${hh(hour)}:00`, decimal(value)] as const),
              }}
            />
          </div>
        )}
      </Panel>

      {days.length > 1 ? (
        <Panel title={s.daily}>
          <div className="px-4 pt-4 pb-2">
            <ColumnChart
              values={dayValues}
              tickLabel={(index) => dayTick(days[index] ?? '')}
              showTick={(index) => (days.length - 1 - index) % every === 0}
              describe={(index) => s.dailyLabel(formatDate(days[index] ?? ''), dayValues[index] ?? 0)}
              summary={s.daily}
              mark={dayValues[busiestDay] ? busiestDay : null}
              table={{
                caption: s.daily,
                head: [s.tableDay, s.tableVisits],
                rows: days.map((day, index) => [formatDate(day), String(dayValues[index] ?? 0)] as const),
              }}
            />
          </div>
        </Panel>
      ) : null}

      <div className="grid gap-8 lg:grid-cols-2">
        <Panel title={s.byMethod}>
          <BarList
            empty={s.hourlyEmpty}
            rows={ranked(data.byMethod).map(([method, value]) => ({
              key: method,
              label: t.access.methods[method] ?? method,
              value,
            }))}
          />
        </Panel>
        <Panel title={s.refusals} aside={data.keysNotReturned > 0 ? s.keysNotReturned(data.keysNotReturned) : undefined}>
          <BarList
            empty={s.refusalsNone}
            rows={ranked(data.refusals).map(([reason, value]) => ({ key: reason, label: refusalText(reason), value }))}
          />
        </Panel>
      </div>
    </div>
  )
}

const Panel = ({ title, aside, children }: { title: string; aside?: string | undefined; children: ReactNode }) => (
  <section className="flex flex-col gap-2">
    <SectionHeader title={title} aside={aside} />
    <div className="overflow-hidden rounded-[16px] bg-surface">{children}</div>
  </section>
)
